import { useAppTheme } from "@hooks/useAppTheme";
import Box from "@memorang/ui/components/Box";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { CursorValue, LayoutChangeEvent } from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, {
	useSharedValue,
	runOnJS,
	useAnimatedStyle,
	withTiming,
} from "react-native-reanimated";

type PodcastProgressBarProps = {
	position: number;
	duration: number;
	onSeek: (position: number) => void;
};

export const PodcastProgressBar = ({
	position,
	duration,
	onSeek,
}: PodcastProgressBarProps) => {
	const theme = useAppTheme();
	const progress = duration > 0 ? position / duration : 0;
	const [width, setWidth] = useState(200);
	const seekProgress = useSharedValue(progress);
	const isDragging = useSharedValue(false);

	useEffect(() => {
		if (!isDragging.value) {
			seekProgress.value = withTiming(progress, { duration: 100 });
		}
	}, [progress, seekProgress, isDragging]);

	const handleLayout = useCallback((e: LayoutChangeEvent) => {
		setWidth(e.nativeEvent.layout.width);
	}, []);

	const handleSeek = useCallback(
		(newProgress: number) => {
			const clampedProgress = Math.max(0, Math.min(1, newProgress));
			onSeek(clampedProgress * duration);
		},
		[duration, onSeek],
	);

	const panGesture = useMemo(
		() =>
			Gesture.Pan()
				.onBegin(() => {
					isDragging.value = true;
				})
				.onUpdate((e) => {
					const newProgress = Math.max(0, Math.min(1, e.x / width));
					seekProgress.value = newProgress;
				})
				.onEnd((e) => {
					const newProgress = Math.max(0, Math.min(1, e.x / width));
					runOnJS(handleSeek)(newProgress);
					isDragging.value = false;
				}),
		[width, handleSeek, seekProgress, isDragging],
	);

	const tapGesture = useMemo(
		() =>
			Gesture.Tap().onEnd((e) => {
				const newProgress = Math.max(0, Math.min(1, e.x / width));
				seekProgress.value = newProgress;
				runOnJS(handleSeek)(newProgress);
			}),
		[width, handleSeek, seekProgress],
	);

	const gesture = useMemo(
		() => Gesture.Race(panGesture, tapGesture),
		[panGesture, tapGesture],
	);

	const progressBarStyle = useAnimatedStyle(() => {
		return {
			width: `${seekProgress.value * 100}%`,
		};
	});

	const thumbStyle = useAnimatedStyle(() => {
		return {
			left: `${seekProgress.value * 100}%`,
		};
	});

	return (
		<GestureDetector gesture={gesture}>
			<Animated.View
				onLayout={handleLayout}
				style={{
					width: "100%",
					height: 24,
					justifyContent: "center",
					cursor: "grab" as unknown as CursorValue,
				}}
			>
				<Box
					width="100%"
					height={3}
					borderRadius={1.5}
					backgroundColor={theme.colors.surfaceVariant}
				>
					<Animated.View
						style={[
							{
								height: "100%",
								borderRadius: 1.5,
								backgroundColor: theme.colors.primary,
							},
							progressBarStyle,
						]}
					/>
					<Animated.View
						style={[
							{
								position: "absolute",
								top: -4,
								width: 12,
								height: 12,
								borderRadius: 6,
								backgroundColor: theme.colors.primary,
								transform: [{ translateX: -6 }],
								shadowColor: "#000",
								shadowOffset: { width: 0, height: 1 },
								shadowOpacity: 0.2,
								shadowRadius: 1,
								elevation: 2,
							},
							thumbStyle,
						]}
					/>
				</Box>
			</Animated.View>
		</GestureDetector>
	);
};
